import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDrag, useDrop } from 'react-dnd';
import { RootState } from '../../store';
import { Position, Player } from '../../types';
import { 
  setSelectedPosition, 
  removePlayer, 
  movePlayer, 
  swapPlayerWithSubstitute,
  moveSubstituteToField,
  toggleFieldOrientation
} from '../../store/teamSlice';
import { useTeamManagement } from '../../hooks/useTeamManagement';
import { PlayerSearchHandle } from '../players/PlayerSearch';

import './SoccerField.css';

// Define item types for dragging
const ItemTypes = {
  PLAYER: 'player',
  SUBSTITUTE: 'substitute'
};



// Define a component for a draggable player
const DraggablePlayer: React.FC<{
  player: Player;
  position: Position;
  onRemove: (player: Player, e: React.MouseEvent) => void;
  onPlayerClick: (player: Player, position: Position) => void;
}> = ({ player, position, onRemove, onPlayerClick }) => {
  const [{isDragging}, drag] = useDrag(() => ({
    type: ItemTypes.PLAYER,
    item: () => ({
      player: {
        ...player,
        position
      }
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    end: (item, monitor) => {
      if (!monitor.didDrop()) {
        // Player was not dropped on a valid target
      }
    }
  }), [player, position]);

  const handlePlayerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPlayerClick(player, position);
  };

  return (
    <div 
      // @ts-ignore - React DnD typing issue
      ref={drag}
      className="player-info"
      style={{ opacity: isDragging ? 0.5 : 1 }}
      onClick={handlePlayerClick}
    >
      <img src={player.imageUrl || 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=?'} alt={player.name || 'Player'} className="player-image" />
      <div className="player-name" title={player.name || 'Player'}>{formatPlayerName(player.name)}</div>
      <button 
        className="remove-player-btn"
        onClick={(e) => onRemove({...player, position}, e)}
        title="Remove player"
      >
        ×
      </button>
    </div>
  );
};

// Helper function to format player name (for display purposes)
const formatPlayerName = (name: string | undefined): string => {
  // Handle undefined or empty name
  if (!name) return 'Player';
  
  // If name is already short, return as is
  if (name.length <= 15) return name;
  
  // For longer names, show first name and last initial
  const nameParts = name.split(' ');
  if (nameParts.length === 1) return name; // Single name
  
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];
  
  return `${firstName} ${lastName.charAt(0)}.`;
};

// Define a component for a position on the field
const PlayerPosition: React.FC<{
  position: Position;
  index: number;
  player?: Player;
  isSelected: boolean;
  onPositionClick: (position: Position) => void;
  onRemovePlayer: (player: Player, e: React.MouseEvent) => void;
  onPlayerClick: (player: Player, position: Position) => void;
  coordinates: { top: string; left: string };
  displayPosition: string;
}> = ({ 
  position, 
  index, 
  player, 
  isSelected, 
  onPositionClick, 
  onRemovePlayer, 
  onPlayerClick,
  coordinates, 
  displayPosition 
}) => {
  const dispatch = useDispatch();
  const teamState = useSelector((state: RootState) => state.team.team);
  const playerObjects = teamState.players;
  
  // Set up drop functionality for each position
  const [{isOver, canDrop}, drop] = useDrop(() => ({
    accept: [ItemTypes.PLAYER, ItemTypes.SUBSTITUTE],
    canDrop: (item: any) => {
      // Add logic to check if this position can accept the dragged item
      return true; // Can be customized with additional rules
    },
    drop: (item: any) => {
      // Check if we're dropping a player or a substitute
      if (item.player) {
        // It's a player from the field
        const draggedPlayer = item.player;
        if (draggedPlayer.position !== position) {
          dispatch(movePlayer({ 
            fromPosition: draggedPlayer.position as Position, 
            toPosition: position 
          }));
        }
      } else if (item.substitute) {
        // It's a substitute from the bench
        
        // Find player in this position, if any
        const selectedPlayer = playerObjects[position];
        
        if (selectedPlayer) {
          // If there's already a player in this position, swap them
          dispatch(swapPlayerWithSubstitute({
            playerId: position, // Use position instead of ID
            substituteId: item.substitute.id,
          }));
        } else {
          // If the position is empty, move the substitute to the field
          dispatch(moveSubstituteToField({
            substituteId: item.substitute.id,
            position: position
          }));
        }
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop()
    })
  }), [position, playerObjects]);
  
  return (
    <div
      key={`${position}-${index}`}
      // @ts-ignore - React DnD typing issue
      ref={drop}
      className={`player-position ${isSelected ? 'selected' : ''} 
               ${player ? 'filled' : 'empty'} 
               ${isOver ? 'drop-hover' : ''} 
               ${isOver && canDrop ? 'valid-drop-target' : ''}`}
      style={{ top: coordinates.top, left: coordinates.left }}
      onClick={() => onPositionClick(position)}
    >
      <div className="position-label">{displayPosition}</div>
      {player && (
        <DraggablePlayer 
          player={player} 
          position={position} 
          onRemove={onRemovePlayer}
          onPlayerClick={onPlayerClick}
        />
      )}
    </div>
  );
};

interface SoccerFieldProps {
  playerSearchRef?: React.RefObject<PlayerSearchHandle | null>;
  onPlayerClick?: (player: Player, position: Position) => void;
}

const SoccerField: React.FC<SoccerFieldProps> = ({ playerSearchRef, onPlayerClick }) => {
  const dispatch = useDispatch();
  const teamState = useSelector((state: RootState) => state.team.team);
  const formation = teamState.formation;
  const selectedPosition = useSelector((state: RootState) => state.team.selectedPosition);
  const fieldOrientation = useSelector((state: RootState) => state.team.fieldOrientation);
  
  // Team management for unsaved changes detection and saving
  const { hasUnsavedChanges, isSaving, saveTeam, isAuthenticated } = useTeamManagement();
  
  // Function to get player by position
  const getPlayerByPosition = (position: Position): Player | undefined => {
    // This is a more reliable way to find a player by position
    // It looks directly at the player object in the position key
    return teamState.players[position] || undefined;
  };

  // Function to get position coordinates on the field based on orientation
  const getPositionCoordinates = (position: Position, index: number): { top: string; left: string } => {
    // Get the base coordinates depending on formation
    let coords: { top: string; left: string } = { top: '50%', left: '50%' };
    
    // Formation-specific positioning for landscape mode
    if (formation.id === '3-5-2') {
      // 3-5-2 Formation - Complete custom positioning
      switch (position) {
        case 'GK': coords = { top: '90%', left: '50%' }; break;
        // 3 center backs
        case 'LCB': coords = { top: '75%', left: '25%' }; break;
        case 'CCB': coords = { top: '70%', left: '50%' }; break;
        case 'RCB': coords = { top: '75%', left: '75%' }; break;
        // Wing backs
        case 'LWB': coords = { top: '58%', left: '10%' }; break;
        case 'RWB': coords = { top: '58%', left: '90%' }; break;
        // Midfielders
        case 'LCM': coords = { top: '45%', left: '30%' }; break;
        case 'CDM': coords = { top: '55%', left: '50%' }; break;
        case 'RCM': coords = { top: '45%', left: '70%' }; break;
        // Strikers
        case 'LST': coords = { top: '25%', left: '35%' }; break;
        case 'RST': coords = { top: '25%', left: '65%' }; break;
      }
    } else if (formation.id === '4-2-3-1') {
      // 4-2-3-1 Formation - Complete custom positioning
      switch (position) {
        case 'GK': coords = { top: '90%', left: '50%' }; break;
        // Back four
        case 'LB': coords = { top: '75%', left: '15%' }; break;
        case 'LCB': coords = { top: '80%', left: '35%' }; break;
        case 'RCB': coords = { top: '80%', left: '65%' }; break;
        case 'RB': coords = { top: '75%', left: '85%' }; break;
        // Double pivot
        case 'LCDM': coords = { top: '65%', left: '38%' }; break;
        case 'RCDM': coords = { top: '65%', left: '62%' }; break;
        // Attacking trio
        case 'LW': coords = { top: '35%', left: '20%' }; break;
        case 'CAM': coords = { top: '30%', left: '50%' }; break;
        case 'RW': coords = { top: '35%', left: '80%' }; break;
        // Striker
        case 'ST': coords = { top: '15%', left: '50%' }; break;
      }
    } else {
      // Base coordinates for all positions and formations
      const basePositions: Record<Position, { top: string; left: string }> = {
        // Goalkeeper
        'GK':  { top: '90%', left: '50%' },
        
        // Defenders
        'LB':  { top: '72%', left: '15%' },
        'RB':  { top: '72%', left: '85%' },
        'CB': { top: '72%', left: '50%' },
        'LCB': { top: '72%', left: '35%' },
        'RCB': { top: '72%', left: '65%' },
        'CCB': { top: '72%', left: '50%' },
        'LWB': { top: '65%', left: '15%' },
        'RWB': { top: '65%', left: '85%' },
        
        // Midfielders
        'CDM':  { top: '58%', left: '50%' },
        'LCDM': { top: '58%', left: '35%' },
        'RCDM': { top: '58%', left: '65%' },
        'CAM':  { top: '38%', left: '50%' },
        'CM':  { top: '50%', left: '50%' },
        'LCM':  { top: '50%', left: '32%' },
        'RCM':  { top: '50%', left: '68%' },
        'CCM':  { top: '50%', left: '50%' },
        'LM':   { top: '50%', left: '15%' },
        'RM':   { top: '50%', left: '85%' },
        
        // Forwards
        'LW':  { top: '28%', left: '15%' },
        'RW':  { top: '28%', left: '85%' },
        'ST':  { top: '25%', left: '50%' },
        'LST': { top: '25%', left: '32%' },
        'RST': { top: '25%', left: '68%' },
        'CF':  { top: '18%', left: '50%' },
      };
      
      // Formation-specific adjustments for other formations
      if (formation.id === '3-4-3') {
        // Adjust for 3-4-3 formation
        switch (position) {
          case 'GK': coords = { top: '90%', left: '50%' }; break;
          // 3 center backs
          case 'LCB': coords = { top: '75%', left: '30%' }; break;
          case 'CCB': coords = { top: '70%', left: '50%' }; break;
          case 'RCB': coords = { top: '75%', left: '70%' }; break;
          // Midfielders
          case 'LM': coords = { top: '55%', left: '20%' }; break;
          case 'LCM': coords = { top: '55%', left: '40%' }; break;
          case 'RCM': coords = { top: '55%', left: '60%' }; break;
          case 'RM': coords = { top: '55%', left: '80%' }; break;
          // Forwards
          case 'LW': coords = { top: '30%', left: '20%' }; break;
          case 'ST': coords = { top: '25%', left: '50%' }; break;
          case 'RW': coords = { top: '30%', left: '80%' }; break;
        }
      } else if (formation.id === '5-3-2') {
        // Adjust for 5-3-2 formation
        switch (position) {
          case 'GK': coords = { top: '90%', left: '50%' }; break;
          // Back five
          case 'LWB': coords = { top: '70%', left: '10%' }; break;
          case 'LCB': coords = { top: '75%', left: '30%' }; break;
          case 'CCB': coords = { top: '70%', left: '50%' }; break;
          case 'RCB': coords = { top: '75%', left: '70%' }; break;
          case 'RWB': coords = { top: '70%', left: '90%' }; break;
          // Three midfielders
          case 'LCM': coords = { top: '50%', left: '30%' }; break;
          case 'CM': coords = { top: '50%', left: '50%' }; break;
          case 'RCM': coords = { top: '50%', left: '70%' }; break;
          // Two strikers
          case 'LST': coords = { top: '25%', left: '35%' }; break;
          case 'RST': coords = { top: '25%', left: '65%' }; break;
        }
      } else {
        // If position is defined in base positions and not overridden by formation-specific adjustments
        coords = basePositions[position] || { top: '50%', left: '50%' };
      }
    }
    
    // If we're in portrait mode, transform the coordinates
    if (fieldOrientation === 'portrait') {
      // Extract percentages as numbers
      const topPercentage = parseFloat(coords.top);
      const leftPercentage = parseFloat(coords.left);
      
      // For portrait mode we need to:
      // 1. Maintain horizontal width (the spread between LB and RB)
      // 2. Increase vertical length (the distance between GK and ST)
      
      // Map the vertical position based on player role
      let newTop;
      
      if (topPercentage >= 85) {
        // Goalkeeper - move to very bottom
        newTop = 90;
      } else if (topPercentage >= 70 && topPercentage < 85) {
        // Defenders - spread between 70-85%
        // Map from [70-85] range to [70-85] range
        newTop = 70 + (topPercentage - 70) * 1.0;
      } else if (topPercentage >= 50 && topPercentage < 70) {
        // Defensive midfielders - spread between 55-70%
        // Map from [50-70] range to [55-70] range
        newTop = 55 + (topPercentage - 50) * 0.75;
      } else if (topPercentage >= 30 && topPercentage < 50) {
        // Central midfielders - spread between 35-55%
        // Map from [30-50] range to [35-55] range
        newTop = 35 + (topPercentage - 30) * 1.0;
      } else if (topPercentage >= 20 && topPercentage < 30) {
        // Attacking midfielders - spread between 25-35%
        // Map from [20-30] range to [25-35] range
        newTop = 25 + (topPercentage - 20) * 1.0;
      } else {
        // Forwards - move to very top 
        // Map from [10-20] range to [10-25] range
        newTop = Math.max(10, Math.min(25, 10 + (topPercentage - 10) * 1.5));
      }
      
      return {
        top: `${leftPercentage}%`, // Maintain horizontal spread
        left: `${100 - newTop}%`   // Stretched vertical positions
      };
    }
    
    return coords;
  };

  // Function to remove player from position
  const handleRemovePlayer = (player: Player, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering position selection
    if (player && player.position) {
      // Ensure we're passing a valid position string that exists in the formation
      const positionExists = formation.positions.includes(player.position as Position);
      if (!positionExists) {
        return;
      }
      
      // Pass the player's position to removePlayer action
      dispatch(removePlayer(player.position));
    }
  };

  // Function to handle position click
  const handlePositionClick = (position: Position) => {
    dispatch(setSelectedPosition(position));
    
    // Focus the search input if it's an empty position and playerSearchRef is provided
    if (!getPlayerByPosition(position) && playerSearchRef?.current) {
      playerSearchRef.current.focusSearchInput();
    }
  };
  
  // Function to convert specific position IDs to display positions
  const getDisplayPosition = (position: Position): string => {
    // Map specific positions to generic display positions
    const positionStr = String(position);
    
    if (positionStr.startsWith('L') || positionStr.startsWith('R') || positionStr.startsWith('C')) {
      // LCB, RCB, CCB -> CB
      if (positionStr.endsWith('CB')) return 'CB';
      
      // LCM, RCM, CCM -> CM
      if (positionStr.endsWith('CM')) return 'CM';
      
      // LCDM, RCDM -> CDM
      if (positionStr.endsWith('CDM')) return 'CDM';
      
      // LST, RST -> ST
      if (positionStr.endsWith('ST')) return 'ST';
    }
    
    // Add more mapping if needed
    if (position === 'LM' && positionStr.includes('WB')) return 'WB';
    if (position === 'RM' && positionStr.includes('WB')) return 'WB';
    
    // Return original position if not a specific variant
    return position;
  };

  // Function to toggle field orientation
  const handleToggleOrientation = () => {
    dispatch(toggleFieldOrientation());
  };

  // Function to handle player click
  const handlePlayerClick = (player: Player, position: Position) => {
    if (onPlayerClick) {
      onPlayerClick(player, position);
    }
  };

  return (
    <div className={`soccer-field ${fieldOrientation}`}>
      {/* Orientation toggle button */}
      <button 
        className="orientation-toggle-btn"
        onClick={handleToggleOrientation}
        title={`Switch to ${fieldOrientation === 'landscape' ? 'portrait' : 'landscape'} view`}
      >
        <span className="orientation-icon">{fieldOrientation === 'landscape' ? '↕' : '↔'}</span>
      </button>
      
      {/* Field markings */}
      <div className="field-center-circle"></div>
      <div className="field-center-line"></div>
      <div className="field-goal top"></div>
      <div className="field-goal bottom"></div>
      <div className="field-penalty-area top"></div>
      <div className="field-penalty-area bottom"></div>
      
      {/* Field drag hint */}
      <div className="field-drag-hint">
        <i className="drag-icon">↕</i> Drag players between positions or to substitutes
      </div>
      
      {/* Player positions */}
      {formation.positions.map((position: Position, index: number) => {
        const player = getPlayerByPosition(position);
        const coords = getPositionCoordinates(position, index);
        const displayPos = getDisplayPosition(position);
        
        return (
          <PlayerPosition
            key={`${position}-${index}`}
            position={position}
            index={index}
            player={player}
            isSelected={selectedPosition === position}
            onPositionClick={handlePositionClick}
            onRemovePlayer={handleRemovePlayer}
            onPlayerClick={handlePlayerClick}
            coordinates={coords}
            displayPosition={displayPos}
          />
        );
      })}

    </div>
  );
};

export default SoccerField; 